import MiscApi from "@/services/api/misc";
import RetailerApi from "@/services/api/retailerApi";
import FileApi from "@/services/api/fileApi";
import { mapGetters } from "vuex";
import VueEditor from "@/plugins/VueEditor/VueEditor";
import BrandMultiSelect from "./brandMultiSelect/brandMultiSelect.vue";
import ScoreSelector from "./scoreSelector/scoreSelector.vue";
import { recommendationsApi } from "@/services/api/recommendationsApi";

const MiscApiService = new MiscApi(),
  RetailerApiService = new RetailerApi(),
  FileApiService = new FileApi();
export default {
  components: {
    VueEditor,
    BrandMultiSelect,
    ScoreSelector,
  },

  data() {
    return {
      origin: window.location.origin,
      storeValues: [],
      storeTypes: [],
      uploadingStoreGalleryImage: false,
      statuses: [],
      balanceLevels: null,
      countries: [],
      requested: false,
      uploadInProgressThumb: false,
      customToolbar: [["bold", "italic", "underline"], ["clean"]],
      model: {
        city: "",
        is_public: false,
        first_name: "",
        last_name: "",
        email: "",
        store_name: "",
        location: "",
        instagram: "",
        year_founded: "",
        bio: null,
        balance_level: "",
        thumbnail_image_url: null,
        balance: "",
        store_gallery: [],
        store_types: [],
        values: [],
        id: null,
        is_approved: false,
        is_declined: false,
        phone_number: "",
        score: null,
      },
      recommendedBrands: [],
      newRecommendedBrands: [],
      fetchRecommendedBrandsInProgress: true,
    };
  },

  async beforeMount() {
    await this.getStoreValues();
    await this.getStoreTypes();

    if (this.isAdmin) {
      this.fetchRecommendedBrands();
      this.getRetailerStatuses();
      this.getCountries();
      this.getRetailer();
    } else {
      this.getInfo();
    }
  },

  methods: {
    toggleProfileVisibility,
    parseStoreValuesTypes,
    selectStoreValue,
    selectStoreType,
    onStoreLocationSelected,
    onStoreGalleryInputChange,
    removeStoreGalleryImage,
    onThumbImageInputChange,
    removeThumbImage,
    copyURL,
    getInfo,
    validate,
    save,
    getCountries,
    getRetailer,
    getRetailerBalanceLevels,
    getRetailerStatuses,
    getStoreValues,
    getStoreTypes,
    approveRetailer,
    declineRetailer,
    handleFavouriteBrandSelectionChange,
    fetchRecommendedBrands,
    onScoreSelect,
  },

  computed: {
    ...mapGetters({
      user: "userState",
    }),

    isAdmin() {
      return this.user.role === "ADMIN";
    },

    currentBalanceLevel() {
      if (!this.balanceLevels) {
        return "";
      }

      const level = this.balanceLevels.find((level) => level.value === this.model.balance_level);
      return level ? level.label : "";
    },

    selectedStoreValues() {
      return this.storeValues.filter((item) => item.selected).map((item) => item.value);
    },

    selectedStoreTypes() {
      return this.storeTypes.filter((item) => item.selected).map((item) => item.value);
    },
  },
  watch: {
    "model.store_url": function (newValue, oldValue) {
      if (newValue !== oldValue) {
        // if the url is changed, make sure it has the prefix. If not, add it.
        if (
          this.model.store_url &&
          !this.model.store_url.startsWith("https://") &&
          !this.model.store_url.startsWith("http://") &&
          !this.model.store_url.startsWith("ftp://")
        ) {
          const newUrl = `https://${this.model.store_url}`;
          this.$set(this.model, "store_url", newUrl);
        }
      }
    },
  },
};

function toggleProfileVisibility() {
  this.$validator.reset();
}

function selectStoreValue(index) {
  this.storeValues[index].selected = !this.storeValues[index].selected;
  this.model.values = this.selectedStoreValues;
}

function selectStoreType(index) {
  this.storeTypes[index].selected = !this.storeTypes[index].selected;
  this.model.store_types = this.selectedStoreTypes;
}

function onStoreLocationSelected(country) {
  this.model.location = country.name;
  this.model.store_location_id = country.id;
}

function copyURL() {
  this.$refs.bulletinURL.select();
  this.$refs.bulletinURL.setSelectionRange(0, this.$refs.bulletinURL.value.length);
  document.execCommand("copy");
}

async function onStoreGalleryInputChange(e) {
  if (!e.target.files.length) {
    return;
  }

  [...e.target.files].forEach(async (file) => {
    if (file.size > this.$utils.getByteSizeFromMegabyteSize(this.$utils.constants.IMAGE_UPLOAD_SIZE_LIMIT_MB)) {
      e.target.value = "";
      return this.$swal({ title: this.$utils.constants.IMAGE_UPLOAD_SIZE_LIMIT_ERROR_MESSAGE });
    }

    this.uploadingStoreGalleryImage = true;
    let formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await FileApiService.uploadFile(formData);
      this.model.store_gallery.push(data.url);
    } catch (e) {
      return this.$swal("Alert", "Something went wrong. Please try again .");
    } finally {
      this.$refs.imageGalleryInput.value = "";
      this.uploadingStoreGalleryImage = false;
    }
  });
}

function removeStoreGalleryImage(index) {
  this.model.store_gallery.splice(index, 1);
}

async function onThumbImageInputChange(e) {
  if (!e.target.files.length) {
    return;
  }
  const previousThumb = this.model.thumbnail_image_url; // use this if upload fails
  this.removeThumbImage();
  const file = e.target.files[0];
  //size check
  if (file.size > this.$utils.getByteSizeFromMegabyteSize(this.$utils.constants.IMAGE_UPLOAD_SIZE_LIMIT_MB)) {
    return this.$swal({ title: this.$utils.constants.IMAGE_UPLOAD_SIZE_LIMIT_ERROR_MESSAGE });
  }
  //begin upload process
  this.uploadInProgressThumb = true;
  let formData = new FormData();
  formData.append("file", file);
  try {
    const { data } = await MiscApiService.uploadRetailerThumb(formData);
    this.model.thumbnail_image_url = data.url;
    this.uploadInProgressThumb = false;
  } catch (e) {
    this.model.thumbnail_image_url = previousThumb;
    this.uploadInProgressThumb = false;
    return this.$swal("Alert", "Something went wrong. Please try again .");
  }
}

function removeThumbImage() {
  this.model.thumbnail_image_url = null;
}

async function getInfo() {
  try {
    const { data: balanceLevels } = await RetailerApiService.fetchRetailerBalanceLevels(),
      response = await MiscApiService.getMeRetailer();

    this.balanceLevels = balanceLevels.options;
    this.model = response.data;
    this.$forceUpdate();
    this.parseStoreValuesTypes();
    this.$validator.reset();
  } catch (error) {
    if (error.response) {
      this.$swal("Attention", error.response.data.error_message);
    } else {
      this.$bugsnagClient.notify(new Error(error));
    }
  }
}

function validate() {
  if (this.model.is_public && (!this.model.store_gallery.length || !this.model.thumbnail_image_url)) {
    this.$swal({
      title: "Attention",
      text: "Make sure that store gallery contains at least 1 image and profile picture has been added.",
    });

    return;
  }

  if (this.model.is_public && (!this.model.values.length || !this.model.store_types.length)) {
    this.$swal({
      title: "Attention",
      text: "Select at least one store value and store type.",
    });

    return;
  }

  this.$validator.validate().then((valid) => {
    if (!valid) {
      this.$swal({
        title: "Attention",
        text: "Please complete all required fields.",
      });

      return;
    }

    this.save();
  });
}

async function save() {
  this.requested = true;

  let request = {
    ...this.model,
  };

  delete request.location;

  try {
    if (this.$route.params.id) {
      await RetailerApiService.updateRetailerAccountById(this.$route.params.id, request);
    } else {
      await RetailerApiService.updateMyRetailerAccount(request);
    }

    if (JSON.stringify(this.newRecommendedBrands) !== JSON.stringify(this.recommendedBrands)) {
      await recommendationsApi.saveRecommendedBrands(this.newRecommendedBrands, this.$route.params.id);
      this.recommendedBrands = this.newRecommendedBrands;
    }

    this.$swal("Success", "Profile updated successfully.");
  } catch (error) {
    this.$swal("Attention", error.response.data.error_message);
  } finally {
    this.requested = false;
  }
}

async function handleStatusChange(previousStatus, nextStatus) {
  if (previousStatus === nextStatus) {
    // disable click if already selected
    return;
  }
  const modal = await this.$swal({
    text: `Are you sure you want to change ${this.model.store_name}'s status from: ${previousStatus} to ${nextStatus}?`,
    title: "Warning",
    showCancelButton: true,
    cancelButtonText: "No",
    confirmButtonText: "Yes",
  });
  const isConfirmed = modal.value;
  this.model.status = isConfirmed ? nextStatus : previousStatus;
  if (isConfirmed) {
    try {
      await RetailerApiService.updateRetailerStatus(this.$route.params.id, { status: nextStatus });
    } catch (error) {
      this.$swal("Attention", error.response.data.error_message, "error");
    }
  }
}

async function getRetailerBalanceLevels() {
  const { data } = await RetailerApiService.fetchRetailerBalanceLevels();
  this.balanceLevels = data.options.map((item) => {
    return { name: item.label, ...item };
  });
}

function getRetailerStatuses() {
  RetailerApiService.getRetailerStatuses()
    .then(({ data }) => {
      data.options.forEach((item) => {
        item.name = item.value;
      });
      this.statuses = data.options;
    })
    .catch((error) => {
      if (error.response) {
        this.$swal("Attention", error.response.data.error_message);
      }
    });
}

function getCountries() {
  MiscApiService.getAllCountries().then(({ data }) => {
    this.countries = data;
  });
}

function getRetailer() {
  RetailerApiService.getRetailerById(this.$route.params.id)
    .then((response) => {
      this.model = response.data;

      if (this.model.is_declined === undefined) {
        this.model.is_declined = false;
      }
      if (this.model.is_approved === undefined) {
        this.model.is_approved = false;
      }

      this.$forceUpdate();

      // parse store values / types
      this.parseStoreValuesTypes();
      this.$validator.reset();
    })
    .catch((error) => {
      if (error.response) {
        this.$swal("Attention", error.response.data.error_message);
      } else {
        this.$bugsnagClient.notify(new Error(error));
      }
    });

  this.getRetailerBalanceLevels();
}

function getStoreValues() {
  RetailerApiService.getStoreValues().then((response) => {
    this.storeValues = response.data.map((item) => ({
      ...item,
      selected: false,
    }));
  });
}

function getStoreTypes() {
  RetailerApiService.getStoreTypes().then(({ data }) => {
    this.storeTypes = data.options.map((item) => ({
      ...item,
      selected: false,
    }));
  });
}

function parseStoreValuesTypes() {
  this.model.store_types.forEach((item) => {
    this.storeTypes.forEach((type) => {
      if (item === type.value) {
        type.selected = true;
      }
    });
  });

  this.model.values.forEach((item) => {
    this.storeValues.forEach((type) => {
      if (item === type.value) {
        type.selected = true;
      }
    });
  });
}

async function approveRetailer() {
  if (this.model.is_approved) {
    return;
  }
  try {
    await RetailerApiService.approvedRetailer(this.model.id);
    this.model.is_approved = true;
    this.model.is_declined = false;
    this.$forceUpdate();
  } catch (error) {
    this.$swal("Attention", error.response.data.error_message);
  }
}

async function declineRetailer() {
  if (this.model.is_declined) {
    return;
  }
  try {
    await RetailerApiService.declineRetailer(this.model.id);
    this.model.is_declined = true;
    this.model.is_approved = false;
    this.$forceUpdate();
  } catch (error) {
    this.$swal("Attention", error.response.data.error_message);
  }
}

async function fetchRecommendedBrands() {
  try {
    const { data } = await recommendationsApi.fetchRecommendedBrands(this.$route.params.id);
    const { brand_ids } = data || {};
    if (brand_ids) {
      this.recommendedBrands = brand_ids;
      this.newRecommendedBrands = brand_ids;
    }
  } finally {
    this.fetchRecommendedBrandsInProgress = false;
  }
}

function handleFavouriteBrandSelectionChange(selectedBrands) {
  this.newRecommendedBrands = selectedBrands;
}

function onScoreSelect(score) {
  this.model.score = score.value;
}
